/**
 * PostBreakdownEditor — inline table editor for `topic.postBreakdown[]`.
 *
 * Each row in the table = one aspect = one master post downstream
 * (`MasterPostHandler.aspectFocus` resolves by `aspectKey`). The number of
 * rows IS the `plannedPostCount` — there is no separate count slider; that
 * field is derived on save (`plannedPostCount = breakdown.length || 1`).
 *
 * Two action buttons in the header:
 *   - "+ Add aspect"          — manual row
 *   - "✨ Generate with AI"    — opens GenerateAspectsDialog (only when userId)
 *
 * Empty state explains WHY aspects matter: each aspect becomes a distinct
 * master post (context / method / case-study / contrast), unfolding ONE topic
 * into a content series instead of a single post.
 *
 * Persists changes via TopicBacklogFirestoreService.update; the Firestore
 * subscription on `topic_backlog/{topicId}` propagates results back to the
 * editor automatically (used by the AI-generate flow — it writes to the doc
 * server-side, no local state mutation needed).
 */

import React, { useEffect, useState } from 'react';
import {
  Stack, Box, Typography, TextField, IconButton, Button, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { TopicBacklogFirestoreService } from '../../services/TopicBacklogFirestoreService';
import { GenerateAspectsDialog } from './GenerateAspectsDialog';
import type { ContentTopic } from '../../types';

interface BreakdownItem {
  aspectKey: string;
  aspectLabel: string;
  aspectAngle?: string;
}

export interface PostBreakdownEditorProps {
  projectId: string;
  topic: ContentTopic;
  authorId?: string | null;
  /** Optional — required only to enable the "✨ Generate with AI" button. */
  userId?: string;
}

const TESTIDS = {
  root:        'topic-post-breakdown',
  addAspect:   'topic-post-breakdown-add',
  aiGenerate:  'aspects-generate-btn',
  table:       'topic-post-breakdown-table',
  emptyState:  'topic-post-breakdown-empty',
  saveBtn:     'topic-post-breakdown-save',
} as const;

const slugify = (s: string): string =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 32);

export const PostBreakdownEditor: React.FC<PostBreakdownEditorProps> = ({
  projectId, topic, authorId, userId,
}) => {
  const initialBreakdown: BreakdownItem[] =
    Array.isArray((topic as any).postBreakdown) ? (topic as any).postBreakdown : [];

  // Local edit state. Re-sync when the upstream topic doc changes (e.g. after
  // AI-generate flow writes to Firestore — `topic` reference updates).
  const [breakdown, setBreakdown] = useState<BreakdownItem[]>(initialBreakdown);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aiDialogOpen, setAiDialogOpen] = useState(false);

  useEffect(() => {
    const fresh: BreakdownItem[] =
      Array.isArray((topic as any).postBreakdown) ? (topic as any).postBreakdown : [];
    setBreakdown(fresh);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topic.id, JSON.stringify((topic as any).postBreakdown ?? [])]);

  const dirty = JSON.stringify(breakdown) !== JSON.stringify(initialBreakdown);

  const updateAspect = (idx: number, patch: Partial<BreakdownItem>) => {
    setBreakdown((prev) => prev.map((b, i) => (i === idx ? { ...b, ...patch } : b)));
  };

  const addAspect = () => {
    setBreakdown((prev) => [
      ...prev,
      {
        aspectKey: `aspect_${prev.length + 1}`,
        aspectLabel: '',
      },
    ]);
  };

  const removeAspect = (idx: number) => {
    setBreakdown((prev) => prev.filter((_, i) => i !== idx));
  };

  const persist = async () => {
    setError(null);
    setSaving(true);
    try {
      const cleaned = breakdown
        .map((b, idx) => ({
          aspectKey: b.aspectKey || `aspect_${idx + 1}`,
          aspectLabel: (b.aspectLabel ?? '').trim() || `Aspect ${idx + 1}`,
          ...(b.aspectAngle && b.aspectAngle.trim() ? { aspectAngle: b.aspectAngle.trim() } : {}),
        }));
      // plannedPostCount is derived: empty list → 1 (single-post topic, no breakdown).
      const plannedPostCount = cleaned.length || 1;
      const service = new TopicBacklogFirestoreService();
      await service.update(projectId, topic.id, {
        plannedPostCount,
        postBreakdown: cleaned,
      } as any);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const isEmpty = breakdown.length === 0;

  return (
    <Box data-testid={TESTIDS.root}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" color="text.secondary">ASPECTS</Typography>
          <Typography variant="body2">
            {isEmpty
              ? 'No aspects yet — this topic produces 1 post.'
              : `${breakdown.length} aspect${breakdown.length === 1 ? '' : 's'} — ${breakdown.length} master post${breakdown.length === 1 ? '' : 's'} will be generated.`}
          </Typography>
        </Box>
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={addAspect}
          data-testid={TESTIDS.addAspect}
        >
          Add aspect
        </Button>
        {userId && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<AutoFixHighIcon />}
            onClick={() => setAiDialogOpen(true)}
            data-testid={TESTIDS.aiGenerate}
          >
            Generate with AI
          </Button>
        )}
      </Stack>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined" data-testid={TESTIDS.table}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 40 }}>#</TableCell>
              <TableCell sx={{ width: '32%' }}>Aspect</TableCell>
              <TableCell>Hook angle (optional override)</TableCell>
              <TableCell sx={{ width: 48 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {isEmpty ? (
              <TableRow>
                <TableCell colSpan={4} sx={{ py: 3 }}>
                  <Stack spacing={0.75} data-testid={TESTIDS.emptyState}>
                    <Typography variant="body2">
                      <strong>Add aspects to unfold this topic into a content series.</strong>
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Each aspect becomes a separate master post — different facet, different
                      hook, different funnel stage. Common breakdowns:
                    </Typography>
                    <Typography variant="caption" color="text.secondary" component="ul" sx={{ pl: 2, m: 0 }}>
                      <li><strong>2 aspects</strong> — bipolar (problem ↔ solution; before ↔ after; myth ↔ reality)</li>
                      <li><strong>3 aspects</strong> — content arc (context → method → case study)</li>
                      <li><strong>4+ aspects</strong> — mini-series with a unique reframe per post</li>
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Without aspects, the topic produces a single master post.
                    </Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            ) : (
              breakdown.map((b, i) => (
                <TableRow key={i} data-testid={`topic-post-breakdown-row-${i}`}>
                  <TableCell sx={{ verticalAlign: 'top', pt: 1.5 }}>
                    <Typography variant="body2" color="text.secondary">{i + 1}</Typography>
                  </TableCell>
                  <TableCell sx={{ verticalAlign: 'top' }}>
                    <TextField
                      size="small"
                      placeholder="e.g. Why pre-mortem matters"
                      value={b.aspectLabel}
                      onChange={(e) => {
                        const label = e.target.value;
                        updateAspect(i, {
                          aspectLabel: label,
                          aspectKey: b.aspectKey?.startsWith('aspect_')
                            ? slugify(label) || `aspect_${i + 1}`
                            : b.aspectKey,
                        });
                      }}
                      fullWidth
                      variant="standard"
                      InputProps={{ disableUnderline: true }}
                    />
                  </TableCell>
                  <TableCell sx={{ verticalAlign: 'top' }}>
                    <TextField
                      size="small"
                      placeholder="Optional — replaces topic.angle for this aspect's master post"
                      value={b.aspectAngle ?? ''}
                      onChange={(e) => updateAspect(i, { aspectAngle: e.target.value })}
                      fullWidth
                      multiline
                      minRows={1}
                      maxRows={3}
                      variant="standard"
                      InputProps={{ disableUnderline: true }}
                    />
                  </TableCell>
                  <TableCell sx={{ verticalAlign: 'top', pt: 1 }}>
                    <IconButton
                      size="small"
                      onClick={() => removeAspect(i)}
                      data-testid={`topic-post-breakdown-delete-${i}`}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {dirty && (
        <Box sx={{ mt: 1.5 }}>
          <Button
            size="small"
            variant="contained"
            onClick={persist}
            disabled={saving}
            data-testid={TESTIDS.saveBtn}
          >
            {saving ? 'Saving…' : 'Save plan'}
          </Button>
        </Box>
      )}

      {userId && (
        <GenerateAspectsDialog
          open={aiDialogOpen}
          onClose={() => setAiDialogOpen(false)}
          projectId={projectId}
          userId={userId}
          authorId={authorId ?? (topic as any).authorId ?? ''}
          topic={topic}
        />
      )}
    </Box>
  );
};
